import { useState } from 'react';
import { Bug, Code2, X } from 'lucide-react';
import { apiService, PackEntityDTO } from '@/services/api.service';
import { ConfigCatalog } from '@/components/config/ConfigCatalog';
import { BedrockModelViewer } from '@/components/config/BedrockModelViewer';
import { PackFileEditor } from '@/components/config/PackFileEditor';
import { EntityEditor } from '@/components/config/EntityEditor';
import styles from '@/components/config/PackDetail.module.scss';

export function EntitiesPage() {
  return (
    <ConfigCatalog<PackEntityDTO>
      title="Entities"
      icon={Bug}
      kind="entity"
      load={async (g) => (await apiService.getPackEntities(g)).data?.entities ?? []}
      keyOf={(e) => e.identifier}
      identifierOf={(e) => e.identifier}
      subtitleOf={(e) => e.pack}
      thumbnailOf={(e) => Object.values(e.textures || {})[0] || null}
      matches={(e, q) => e.identifier.toLowerCase().includes(q) || e.pack.toLowerCase().includes(q)}
      sourceOf={(e) => e.source ?? e.rpSource}
      statusOf={(e) => (
        <span className={`${styles.chip} ${e.geometry ? styles.chipPrimary : ''}`}>
          {e.geometry ? '3D' : e.rpSource ? 'RP' : 'BP'}
        </span>
      )}
      renderDetail={(e, g, reload, group, visible) => (
        <EntityDetail entity={e} groupId={g} reload={reload} readonlyResource={!!group?.resourceReadonly} visible={visible !== false} />
      )}
    />
  );
}

function EntityDetail({ entity, groupId, reload, readonlyResource, visible }: { entity: PackEntityDTO; groupId: string; reload: () => void; readonlyResource: boolean; visible: boolean }) {
  const primary = Object.values(entity.textures || {})[0] || null;
  const rawPath = entity.source || entity.rpSource;
  const [showRaw, setShowRaw] = useState(false);
  // Box de collision (width/height en blocs), éditée dans l'overlay du viewer.
  const [colW, setColW] = useState(entity.collisionWidth != null ? String(entity.collisionWidth) : '');
  const [colH, setColH] = useState(entity.collisionHeight != null ? String(entity.collisionHeight) : '');
  const cw = colW === '' ? 0 : Number(colW);
  const ch = colH === '' ? 0 : Number(colH);
  // -> BoxSpec en pixels Bedrock (16 px = 1 bloc), centrée sur l'entité, pieds à y=0.
  const collisionBox = cw > 0 && ch > 0
    ? { mode: 'custom', origin: [-cw * 8, 0, -cw * 8], size: [cw * 16, ch * 16, cw * 16] }
    : null;
  // Aperçu : sélection courante + fichiers en attente (géo/texture).
  const [preview, setPreview] = useState<{
    textureUrl: string | null;
    texturePath: string | null;
    geometryText: string | null;
    geometryFile: string | null;
    geometryId: string;
  }>({ textureUrl: null, texturePath: null, geometryText: null, geometryFile: null, geometryId: '' });

  return (
    <div>
      <div className={styles.head}>
        <div>
          <div className={styles.name}>{entity.identifier}</div>
          <div className={styles.pack}>{entity.pack}</div>
        </div>
        <div className={styles.chips}>
          {entity.geometry && <span className={`${styles.chip} ${styles.chipPrimary}`}>Géométrie</span>}
          {rawPath && (
            <button className={styles.editBtn} onClick={() => setShowRaw((v) => !v)}>
              {showRaw ? <X size={14} /> : <Code2 size={14} />}
              {showRaw ? 'Fermer' : 'Avancé (JSON)'}
            </button>
          )}
        </div>
      </div>

      {visible && (
        <div className={styles.viewerWrap}>
        <BedrockModelViewer
          groupId={groupId}
          geometryFile={preview.geometryText ? null : preview.geometryFile ?? entity.geometryFile}
          geometryId={preview.geometryId || entity.geometry}
          texturePath={preview.textureUrl ? null : preview.texturePath ?? primary}
          overrideTextureUrl={preview.textureUrl}
          overrideGeometryText={preview.geometryText}
          standing
          collisionBox={collisionBox}
        />
        <div className={styles.boxOverlay}>
          <div className={styles.boxTitle}><span className={styles.boxDotCol} /> Boîte de collision</div>
          <label className={styles.overlayField}>
            <span>Largeur (blocs)</span>
            <input className={styles.overlayInput} type="number" min={0} step={0.05} value={colW} onChange={(e) => setColW(e.target.value)} placeholder="ex. 0.6" />
          </label>
          <label className={styles.overlayField}>
            <span>Hauteur (blocs)</span>
            <input className={styles.overlayInput} type="number" min={0} step={0.05} value={colH} onChange={(e) => setColH(e.target.value)} placeholder="ex. 1.8" />
          </label>
        </div>
        </div>
      )}

      {entity.source ? (
        <EntityEditor
          entity={entity}
          groupId={groupId}
          onSaved={reload}
          readonlyResource={readonlyResource}
          collisionWidth={cw > 0 ? cw : null}
          collisionHeight={ch > 0 ? ch : null}
          onPreviewOverride={setPreview}
        />
      ) : (
        <div className={styles.metaGrid}>
          <div className={styles.metaRow}>
            <span className={styles.metaLabel}>Édition</span>
            <span className={styles.metaValue}>Entité sans behavior — édition via Avancé (JSON)</span>
          </div>
        </div>
      )}

      {showRaw && rawPath && <PackFileEditor groupId={groupId} path={rawPath} />}
    </div>
  );
}
